import { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import axios, { AxiosError } from 'axios';
import { emptySalesLinkConfig, normalizeTemplateId, type SalesLinkConfig } from '@/lib/sales-link';

export interface ProfileSettings {
  name: string;
  email: string;
  whatsappNumber: string;
}

export interface ProfileResponse extends ProfileSettings {
  id: string;
  role: string;
  onboardingStatus: string;
  pendingEmailChange?: boolean;
  pendingEmail?: string;
  message?: string;
}

export interface StoreAddress {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface SocialLinks {
  facebook?: string;
  instagram?: string;
  twitter?: string;
  tiktok?: string;
  youtube?: string;
  website?: string;
}

export interface BusinessHours {
  day: string;
  open: boolean;
  openTime: string;
  closeTime: string;
}

export interface StoreDetails {
  storeName: string;
  businessName: string;
  businessType: string;
  whatsappNumber: string;
  currency: string;
  logo: string;
  url: string;
  customDomain?: string;
  premiumDomainStatus?: 'not_requested' | 'pending' | 'active' | 'failed';
  address?: StoreAddress;
  socialLinks?: SocialLinks;
  businessHours?: BusinessHours[];
  businessDescription?: string;
  seo?: StoreSEO;
}

export interface StoreSEO {
  title: string;
  description: string;
  keywords: string;
  ogImage?: string;
}

export interface DeliveryLocation {
  name: string;
  price: number;
}

export interface BankAccount {
  accountName: string;
  accountNumber: string;
  bankName: string;
}

export interface ProductDeliveryOptions {
  enableDelivery: boolean;
  enablePickup: boolean;
  deliveryLocations: DeliveryLocation[];
}



export interface CheckoutOptions {
  productDelivery?: {
    enableDelivery?: boolean;
    enablePickup?: boolean;
    deliveryLocations?: Array<{
      name: string;
      price: number;
    }>;
  };

  paymentOptions?: {
    bankTransfer?: {
      enabled?: boolean;
      accounts?: BankAccount[];
      accountInfo?: string;
    };
    cashOnDelivery?: {
      enabled?: boolean;
    };
    manualPayment?: {
      enabled?: boolean;
      instructions?: string;
    };
  };
  whatsapp?: {
    enabled?: boolean;
  };
  guestCheckout?: {
    enabled?: boolean;
    autoSaveCustomer?: boolean;
    requireAccount?: boolean;
  };
}

export interface StoreCustomization {
  template?: 'simple' | 'elegant' | 'compact' | 'modern' | 'retro' | 'custom';
  salesLink?: SalesLinkConfig;
  theme?: string;
  colors?: {
    primary: string;
    secondary: string;
    accent: string;
  };
  layout?: string;
  hero?: {
    title: string;
    subtitle: string;
  };
  banner?: {
    enabled: boolean;
    imageUrl: string;
    imageData?: string;
  };
  announcements?: {
    enabled: boolean;
    text: string;
    backgroundColor: string;
    textColor: string;
  };
  layoutOptions?: {
    showHero: boolean;
    showCategories: boolean;
    showSearch: boolean;
    showSocialLinks: boolean;
  };
}

export interface StoreManager {
  _id: string;
  name: string;
  email: string;
  role: string;
  permissions?: string[];
  storePermissions: string[];
}

export interface BookingSettings {
  enabled: boolean;
  disableProducts?: boolean;
}

export interface SettingsContextType {
  profile: ProfileSettings;
  storeDetails: StoreDetails;
  checkoutOptions: CheckoutOptions;
  customization: StoreCustomization;
  bookingSettings: BookingSettings;
  managers: StoreManager[];
  
  // Loading states
  isLoading: boolean;
  isUpdating: boolean;
  
  // Section 1: Profile
  updateProfile: (data: ProfileSettings) => Promise<void>;
  verifyEmailChange: (otp: string) => Promise<void>;
  resendEmailVerification: () => Promise<void>;
  cancelEmailChange: () => Promise<void>;
  pendingEmailChange: boolean;
  pendingEmail: string | null;
  
  // Section 2: Store Details
  updateStoreDetails: (data: Partial<StoreDetails>) => Promise<void>;
  
  // Section 3: Checkout Options
  updateCheckoutOptions: (data: any) => Promise<void>;
  
  // Section 4: Store Customization
  updateCustomization: (data: Partial<StoreCustomization>) => Promise<StoreCustomization>;
  
  // Section 5: Store Managers
  getManagers: () => Promise<void>;
  inviteManager: (email: string, permissions: string[]) => Promise<void>;
  removeManager: (managerId: string) => Promise<void>;
  updateManagerPermissions: (managerId: string, permissions: string[]) => Promise<void>;
  
  // Section 6: Booking Settings
  updateBookingSettings: (data: Partial<BookingSettings>) => Promise<void>;
  
  // Utility
  reloadSettings: () => Promise<void>;
}

const initialProfileState: ProfileSettings = {
  name: '',
  email: '',
  whatsappNumber: '',
};

const initialStoreDetailsState: StoreDetails = {
  storeName: '',
  businessName: '',
  businessType: '',
  whatsappNumber: '',
  currency: '',
  logo: '',
  url: '',
};

const initialCheckoutOptionsState: CheckoutOptions = {
  productDelivery: {
    enableDelivery: true,
    enablePickup: true,
    deliveryLocations: [],
  },

  paymentOptions: {
    bankTransfer: {
      enabled: false,
      accounts: [],
      accountInfo: '',
    },
    cashOnDelivery: {
      enabled: false,
    },
    manualPayment: {
      enabled: false,
      instructions: '',
    },
  },
  whatsapp: {
    enabled: true,
  },
  guestCheckout: {
    enabled: true,
    autoSaveCustomer: true,
    requireAccount: false,
  },
};

const initialCustomizationState: StoreCustomization = {
  template: 'simple',
  salesLink: emptySalesLinkConfig(),
  theme: 'default',
  colors: {
    primary: '#3b82f6',
    secondary: '#6366f1',
    accent: '#f43f5e',
  },
  layout: 'standard',
  hero: {
    title: 'Discover Our Collection',
    subtitle: 'Explore our carefully curated selection of premium products, designed to enhance your lifestyle and meet your needs.',
  },
  banner: {
    enabled: false,
    imageUrl: '',
  },
  announcements: {
    enabled: false,
    text: 'Welcome to our store! Check out our latest products.',
    backgroundColor: '#fef3c7',
    textColor: '#92400e',
  },
  layoutOptions: {
    showHero: true,
    showCategories: true,
    showSearch: true,
    showSocialLinks: true,
  },
};

const initialBookingSettingsState: BookingSettings = {
  enabled: false,
  disableProducts: false,
};

const mergeCheckoutOptionsState = (incoming?: CheckoutOptions): CheckoutOptions => {
  return {
    productDelivery: {
      ...initialCheckoutOptionsState.productDelivery,
      ...incoming?.productDelivery,
      deliveryLocations: incoming?.productDelivery?.deliveryLocations ?? initialCheckoutOptionsState.productDelivery?.deliveryLocations ?? [],
    },
    paymentOptions: {
      ...initialCheckoutOptionsState.paymentOptions,
      ...incoming?.paymentOptions,
      bankTransfer: {
        ...initialCheckoutOptionsState.paymentOptions?.bankTransfer,
        ...incoming?.paymentOptions?.bankTransfer,
        accounts: incoming?.paymentOptions?.bankTransfer?.accounts ?? [],
        accountInfo: incoming?.paymentOptions?.bankTransfer?.accountInfo ?? initialCheckoutOptionsState.paymentOptions?.bankTransfer?.accountInfo ?? '',
      },
      cashOnDelivery: {
        ...initialCheckoutOptionsState.paymentOptions?.cashOnDelivery,
        ...incoming?.paymentOptions?.cashOnDelivery,
      },
      manualPayment: {
        ...initialCheckoutOptionsState.paymentOptions?.manualPayment,
        ...incoming?.paymentOptions?.manualPayment,
      },
    },
    whatsapp: {
      ...initialCheckoutOptionsState.whatsapp,
      ...incoming?.whatsapp,
    },
    guestCheckout: {
      ...initialCheckoutOptionsState.guestCheckout,
      ...incoming?.guestCheckout,
    },
  };
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, currentStore } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  
  const [profile, setProfile] = useState<ProfileSettings>(initialProfileState);
  const [storeDetails, setStoreDetails] = useState<StoreDetails>(initialStoreDetailsState);
  const [checkoutOptions, setCheckoutOptions] = useState<CheckoutOptions>(initialCheckoutOptionsState);
  const [customization, setCustomization] = useState<StoreCustomization>(initialCustomizationState);
  const [bookingSettings, setBookingSettings] = useState<BookingSettings>(initialBookingSettingsState);
  const [managers, setManagers] = useState<StoreManager[]>([]);
  
  const [pendingEmailChange, setPendingEmailChange] = useState<boolean>(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  
  // Reset all settings when store changes
  useEffect(() => {
    if (isAuthenticated && currentStore) {
      // Reset state to initial values when store changes
      setProfile(initialProfileState);
      setStoreDetails(initialStoreDetailsState);
      setCheckoutOptions(initialCheckoutOptionsState);
      setCustomization(initialCustomizationState);
      setBookingSettings(initialBookingSettingsState);
      setManagers([]);
      
      // Load new settings
      fetchSettings();
      getManagers();
    }
  }, [isAuthenticated, currentStore?._id]); // Use _id to detect actual store changes
  
  const fetchSettings = async () => {
    if (!currentStore) return;
    
    setIsLoading(true);
    try {
      const response = await api.get('/api/settings');
      
      if (response.data.profile) {
        setProfile({
          name: response.data.profile.name || '',
          email: response.data.profile.email || '',
          whatsappNumber: response.data.profile.whatsappNumber || '',
        });
      }
      
      if (response.data.storeDetails) {
        setStoreDetails(response.data.storeDetails);
      }
      
      if (response.data.checkoutOptions) {
        setCheckoutOptions(mergeCheckoutOptionsState(response.data.checkoutOptions));
      }
      
      if (response.data.customization) {
        // Merge with defaults to ensure all fields are present
        const mergedCustomization = {
          ...initialCustomizationState,
          ...response.data.customization,
          colors: {
            ...initialCustomizationState.colors,
            ...response.data.customization.colors,
          },
          hero: {
            ...initialCustomizationState.hero,
            ...response.data.customization.hero,
          },
          banner: {
            ...initialCustomizationState.banner,
            ...response.data.customization.banner,
          },
          announcements: {
            ...initialCustomizationState.announcements,
            ...response.data.customization.announcements,
          },
          layoutOptions: {
            ...initialCustomizationState.layoutOptions,
            ...response.data.customization.layoutOptions,
          },
          salesLink: {
            ...emptySalesLinkConfig(),
            ...response.data.customization.salesLink,
            templates: response.data.customization.salesLink?.templates || [],
          },
        };
        setCustomization(mergedCustomization);
      } else {
        setCustomization(initialCustomizationState);
      }
      
      if (response.data.bookingSettings) {
        setBookingSettings({
          ...initialBookingSettingsState,
          ...response.data.bookingSettings,
        });
      } else {
        setBookingSettings(initialBookingSettingsState);
      }
    } catch (error) {
      toast.error('Failed to load settings');
      console.error('Error loading settings:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Exposed function to reload settings data
  const reloadSettings = async () => {
    await fetchSettings();
    await getManagers();
  };
  
  // Helper function to handle API errors
  const handleApiError = (error: unknown, defaultMessage: string) => {
    console.error('API Error:', error);
    const errorMessage = 
      error && typeof error === 'object' && 'message' in error 
        ? String(error.message)
        : defaultMessage;
    toast.error(error.response?.data?.message || errorMessage);
  };
  
  // Section 1: Profile
  const updateProfile = async (data: ProfileSettings) => {
    setIsUpdating(true);
    try {
      const response = await api.patch<ProfileResponse>('/api/settings/profile', data);
      
      // Check if there's a pending email change
      if (response.data.pendingEmailChange) {
        setPendingEmailChange(true);
        setPendingEmail(response.data.pendingEmail || null);
        toast.success(response.data.message || 'Verification code sent to your new email');
      } else {
        setProfile({
          name: response.data.name,
          email: response.data.email,
          whatsappNumber: response.data.whatsappNumber || '',
        });
        toast.success('Profile updated successfully');
      }
    } catch (error) {
      handleApiError(error, 'Failed to update profile');
    } finally {
      setIsUpdating(false);
    }
  };
  
  const verifyEmailChange = async (otp: string) => {
    setIsUpdating(true);
    try {
      const response = await api.post<ProfileResponse>('/api/settings/profile/verify-email', { otp });
      
      setProfile({
        name: response.data.name,
        email: response.data.email,
        whatsappNumber: response.data.whatsappNumber || '',
      });
      
      setPendingEmailChange(false);
      setPendingEmail(null);
      
      toast.success(response.data.message || 'Email updated successfully');
    } catch (error) {
      handleApiError(error, 'Failed to verify email');
    } finally {
      setIsUpdating(false);
    }
  };
  
  const resendEmailVerification = async () => {
    setIsUpdating(true);
    try {
      const response = await api.post('/api/settings/profile/resend-verification');
      toast.success('Verification code sent to your new email');
    } catch (error) {
      handleApiError(error, 'Failed to resend verification code');
    } finally {
      setIsUpdating(false);
    }
  };
  
  const cancelEmailChange = async () => {
    setIsUpdating(true);
    try {
      const response = await api.post('/api/settings/profile/cancel-email-change');
      setPendingEmailChange(false);
      setPendingEmail(null);
      toast.success('Email change cancelled');
    } catch (error) {
      handleApiError(error, 'Failed to cancel email change');
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Section 2: Store Details
  const updateStoreDetails = async (data: Partial<StoreDetails>) => {
    if (!currentStore) return;
    
    setIsUpdating(true);
    try {
      const response = await api.patch('/api/settings/store-details', data);
      setStoreDetails(prev => ({ ...prev, ...data }));
      toast.success('Store details updated successfully');
    } catch (error) {
      handleApiError(error, 'Failed to update store details');
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Section 3: Checkout Options
  const updateCheckoutOptions = async (data: any) => {
    if (!currentStore) return;
    
    setIsUpdating(true);
    try {
      const response = await api.patch('/api/settings/checkout-options', data);
      
      // Update local state with the new settings
      setCheckoutOptions(prev => mergeCheckoutOptionsState(response.data || prev));
      
      toast.success('Checkout options updated successfully');
      return response.data;
    } catch (error) {
      toast.error('Failed to update checkout options');
      console.error('Error updating checkout options:', error);
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Section 4: Store Customization
  const updateCustomization = async (data: Partial<StoreCustomization>) => {
    if (!currentStore) {
      throw new Error('No store selected');
    }
    
    setIsUpdating(true);
    try {
      const response = await api.patch('/api/settings/customization', data);
      const savedBanner = response.data?.banner;
      const banner = data.banner
        ? {
            enabled: savedBanner?.enabled ?? data.banner.enabled ?? customization.banner?.enabled ?? false,
            imageUrl:
              savedBanner?.imageUrl ||
              (data.banner.imageUrl && !data.banner.imageUrl.startsWith('data:')
                ? data.banner.imageUrl
                : customization.banner?.imageUrl) ||
              '',
          }
        : customization.banner;
      const salesLink = data.salesLink
        ? { activeTemplateId: normalizeTemplateId(data.salesLink.activeTemplateId), templates: [] }
        : customization.salesLink;
      const nextCustomization: StoreCustomization = { ...customization, ...data, banner, salesLink };
      setCustomization(nextCustomization);
      toast.success('Sales link updated');
      return nextCustomization;
    } catch (error) {
      toast.error('Failed to update store customization');
      console.error('Error updating store customization:', error);
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Section 5: Store Managers
  const getManagers = async () => {
    if (!currentStore) {
      setManagers([]);
      return;
    }
    
    try {
      const response = await api.get('/api/settings/managers');
      setManagers(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching managers:', error);
      throw error;
    }
  };
  
  const inviteManager = async (email: string, permissions: string[]) => {
    if (!currentStore) return;
    
    setIsUpdating(true);
    try {
      const response = await api.post('/api/settings/managers/invite', { email, permissions });
      toast.success('Invitation sent successfully');
      await getManagers(); // Refresh the list
      return response.data;
    } catch (error) {
      toast.error('Failed to invite manager');
      console.error('Error inviting manager:', error);
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };
  
  const removeManager = async (managerId: string) => {
    if (!currentStore) return;
    
    setIsUpdating(true);
    try {
      const response = await api.delete(`/api/settings/managers/${managerId}`);
      toast.success('Manager removed successfully');
      setManagers(prev => prev.filter(manager => manager._id !== managerId));
      return response.data;
    } catch (error) {
      toast.error('Failed to remove manager');
      console.error('Error removing manager:', error);
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };
  
  const updateManagerPermissions = async (managerId: string, permissions: string[]) => {
    if (!currentStore) return;
    
    setIsUpdating(true);
    try {
      const response = await api.patch(`/api/settings/managers/${managerId}/permissions`, { permissions });
      toast.success('Permissions updated successfully');
      
      // Update the manager in the list
      setManagers(prev => 
        prev.map(manager => 
          manager._id === managerId 
            ? { ...manager, storePermissions: permissions } 
            : manager
        )
      );
      
      return response.data;
    } catch (error) {
      toast.error('Failed to update permissions');
      console.error('Error updating permissions:', error);
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };
  
  const updateBookingSettings = async (data: Partial<BookingSettings>) => {
    if (!currentStore) return;
    
    setIsUpdating(true);
    try {
      const response = await api.patch('/api/settings/booking-settings', data);
      setBookingSettings(prev => ({ ...prev, ...response.data }));
      toast.success('Booking settings updated successfully');
      return response.data;
    } catch (error) {
      toast.error('Failed to update booking settings');
      console.error('Error updating booking settings:', error);
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };
  
  const value = {
    profile,
    storeDetails,
    checkoutOptions,
    customization,
    bookingSettings,
    managers,
    
    isLoading,
    isUpdating,
    
    // Methods
    updateProfile,
    verifyEmailChange,
    resendEmailVerification,
    cancelEmailChange,
    pendingEmailChange,
    pendingEmail,
    updateStoreDetails,
    updateCheckoutOptions,
    updateCustomization,
    getManagers,
    inviteManager,
    removeManager,
    updateManagerPermissions,
    updateBookingSettings,
    
    // Utility
    reloadSettings
  };
  
  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}; 